import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Alert, Box, Chip, CircularProgress, LinearProgress, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';

import { fetchRequest as fetchRuns } from '../../redux/actions/discovery/fleetRuns';
import { fetchRequest as runNow, clearState as clearRunNow } from '../../redux/actions/discovery/fleetRunNow';
import useRealtimeRefresh, { eventPrefix } from '../../realtime/useRealtimeRefresh';
import StatusChip from '../common/StatusChip';
import { CMButtonOutlinedSmall } from '../../containers/styledComponents';

const RUN_TONES = {
  queued: 'info', running: 'info', reducing: 'info',
  completed: 'success', failed: 'error',
};

const fmtWhen = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch (e) { return '—'; }
};

const FleetRunsPanel = ({
  goalId, goal,
  runs, runsFetch,
  runNowState, runNowStart, runNowClear,
}) => {
  useEffect(() => {
    runsFetch({ goalId });
    return () => runNowClear();
  }, [goalId, runsFetch, runNowClear]);

  useRealtimeRefresh(
    eventPrefix('goal.fleet.run.'),
    () => runsFetch({ goalId }),
  );

  const list = runs?.response || [];
  const active = list.some((r) => ['queued', 'running', 'reducing'].includes(r.status));
  const hasModel = Boolean((goal?.fleet || {}).model);

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
          Scoring runs
        </Typography>
        <Tooltip
          title={!hasModel
            ? 'Available once the bake-off has picked this fleet\u2019s model'
            : active ? 'A run is already in progress' : 'Re-score the whole fleet now (charged like a scheduled run)'}
          arrow
        >
          <span>
            <CMButtonOutlinedSmall
              startIcon={runNowState?.isLoading
                ? <CircularProgress size={14} color="inherit" />
                : <ReplayIcon />}
              disabled={!hasModel || active || runNowState?.isLoading}
              onClick={() => runNowStart({ goalId })}
            >
              Score now
            </CMButtonOutlinedSmall>
          </span>
        </Tooltip>
      </Stack>

      {runNowState?.error && (
        <Alert severity="error" onClose={runNowClear}>{String(runNowState.error)}</Alert>
      )}

      {runs?.isLoading && !list.length ? (
        <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={22} /></Stack>
      ) : !list.length ? (
        <Alert severity="info">No runs yet — the first one starts right after the bake-off completes.</Alert>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Started</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell align="right">Scored</TableCell>
                <TableCell align="right">Coverage</TableCell>
                <TableCell>Origin</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((run) => {
                const running = ['queued', 'running', 'reducing'].includes(run.status);
                const pct = run.chunks_total
                  ? Math.round(((run.chunks_done || 0) / run.chunks_total) * 100) : 0;
                return (
                  <TableRow key={run.run_id} hover>
                    <TableCell>
                      <Typography variant="body2">{fmtWhen(run.created_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={run.status} tone={RUN_TONES[run.status] || 'neutral'} />
                      {run.status === 'failed' && run.error && (
                        <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                          {String(run.error)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      {running ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <LinearProgress variant="determinate" value={pct} sx={{ flex: 1, borderRadius: 1 }} />
                          <Typography variant="caption">{pct}%</Typography>
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {run.status === 'completed' ? `Finished ${fmtWhen(run.finished_at)}` : '—'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {(run.members_scored ?? 0).toLocaleString()} / {(run.members_total ?? 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {run.stats?.coverage != null ? `${Math.round(run.stats.coverage * 100)}%` : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small" variant="outlined"
                        label={run.origin === 'user' ? 'Manual' : run.origin === 'scenario' ? 'Scenario' : 'Scheduled'}
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Stack>
  );
};

const mapStateToProps = (state) => ({
  runs: state.discovery_fleet_runs,
  runNowState: state.discovery_fleet_run_now,
});

const mapDispatchToProps = (dispatch) => ({
  runsFetch: (payload) => dispatch(fetchRuns(payload)),
  runNowStart: (payload) => dispatch(runNow(payload)),
  runNowClear: () => dispatch(clearRunNow()),
});

export default connect(mapStateToProps, mapDispatchToProps)(FleetRunsPanel);
